import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { omit } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FileUpload,
    IconButton,
    ResourceChip,
    Stack,
    styled,
    Text,
} from 'shell/components';
import { uploadFile } from 'utils/file';
import { isAllowedMimeType, lowercaseExtension } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const UploadFileDialog = ({
    allowedFileTypes,
    description,
    error,
    max = 0, // 0 -> unlimited
    open,
    title,
    uploadButtonText: _uploadButtonText,
    onClickUpload,
    onClose,
}) => {
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const { t } = useTranslation('assignment');

    const [loading, setLoading] = useState(false);
    const [files, setFiles] = useState([]);
    const [_error, _setError] = useState('');

    const canStillUploadMore = max === 0 ? true : files.length < max;
    const allowMulti = max !== 1;
    const hasMaxLimit = max !== 0;
    const hasError = Boolean(error) || Boolean(_error);
    const hasReachedLimit = files.length === max;
    const numRemainingUploads = max - files.length;
    const uploadButtonText = _uploadButtonText || t('common:controls.upload', 'Upload');

    const handleClose = () => {
        if (files.some((file) => file.loading)) return;
        onClose();
        setTimeout(() => {
            setFiles([]);
            setLoading(false);
        }, 150);
    };

    const upload = (file, onProgress) => {
        if (!file.data.size) throw new Error('File size is 0.');
        return new Promise((resolve, reject) => {
            uploadFile(
                file.fileData,
                ({ percent }) => onProgress(percent),
                (data) => resolve(data),
                (error) => {
                    console.error(error);
                    reject(error);
                },
                auth.owner_account_id
            );
        });
    };

    const handleChange = async (selectedFiles) => {
        if (hasMaxLimit && files.length + selectedFiles.length > max) {
            _setError(t('error.files-exceed-upload-limit', 'Uploads exceed the file upload limit.'));
            return;
        } else if (
            !Array.from(selectedFiles).every((file) => {
                return file.size <= 500 * 1000000; // 500MB
            })
        ) {
            _setError(t('error.files-too-large-500', 'Each file must be smaller than 500MB.'));
            return;
        } else {
            _setError('');
        }

        if (!selectedFiles.length) {
            return setDialog({
                open: true,
                message: t('dialog.upload-file-error.title', 'Error Detected'),
                description: t(
                    'dialog.upload-file-error.description',
                    'Oops! There was an error uploading the file. Please make sure the file you are trying to upload is not in a ZIP folder.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.ok', 'Ok'),
                cancelButtonText: '',
                onAction: () => closeDialog(),
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }

        const timestamp = new Date().getTime();
        selectedFiles = Array.from(selectedFiles).map((file) => ({
            id: uuid(),
            active: true,
            created_on: timestamp,
            modified_on: timestamp,
            data: {
                name: lowercaseExtension(file.name),
                type: file.type,
                size: file.size,
            },
            fileData: file,
            loading: true,
            loadingValue: 0,
        }));

        if (
            selectedFiles.some((file) => {
                const fileType = file.data.type;
                return !isAllowedMimeType(fileType, allowedFileTypes);
            })
        ) {
            setDialog({
                open: true,
                message: t('dialog.upload-unsupported-file-type.title', 'Upload Failed Due to Unsupported Files'),
                description: (
                    <Stack spacing={4}>
                        <Text>
                            {t(
                                'dialog.upload-unsupported-file-type.description-1',
                                'Sorry, one or more uploaded files are in an unsupported format.'
                            )}
                        </Text>
                        <Text>
                            {t(
                                'dialog.upload-unsupported-file-type.description-2',
                                'Please try again using the following file types: .3gp, .asf, .avi, .csv, .doc, .docx, .flv, .m4v, .mov, .mp4, .mpeg, .mpg, .pdf, .ppt, .pptx, .qt, .wmv, .xls, .xlsx'
                            )}
                        </Text>
                    </Stack>
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.ok', 'Ok'),
                cancelButtonText: '',
                onAction: () => closeDialog(),
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
            return;
        }

        setFiles((current) => [...current, ...selectedFiles]);

        for (const file of selectedFiles.filter((file) => file.loading)) {
            try {
                const data = await upload(file, (percent) => {
                    setFiles((current) => {
                        return current.map((currentFile) => {
                            return currentFile.id === file.id
                                ? {
                                      ...currentFile,
                                      loadingValue: Math.round(percent),
                                  }
                                : currentFile;
                        });
                    });
                });
                setFiles((current) => {
                    return current.map((currentFile) => {
                        return currentFile.id === file.id
                            ? {
                                  ...omit(currentFile, ['data', 'fileData']),
                                  data: {
                                      ...currentFile.data,
                                      ...data,
                                  },
                                  loading: false,
                              }
                            : currentFile;
                    });
                });
            } catch (e) {
                console.error(e);
                setDialog({
                    open: true,
                    message: t('dialog.upload-file-error.title', 'Error Detected'),
                    description: t(
                        'dialog.upload-file-error.description',
                        'Oops! There was an error uploading the file. Please make sure the file you are trying to upload is not in a ZIP folder.'
                    ),
                    note: '',
                    variant: 'destructive',
                    actionButtonText: t('common:controls.ok', 'Ok'),
                    cancelButtonText: '',
                    onAction: () => closeDialog(),
                    onCancel: () => closeDialog(),
                    onClose: () => closeDialog(),
                });
                return setFiles([]);
            }
        }
    };

    const handleSelect = ({ key }, file) => {
        if (key === 'delete') {
            if (files.length === 1 && files[0].id === file.id) {
                setFiles([]);
            } else {
                setFiles((current) => current.filter(({ id }) => id !== file.id));
            }
        }
    };

    const handleClickUpload = () => {
        if (loading) return;
        setLoading(true);
        onClickUpload(files.map((file) => omit(file, ['loading', 'loadingValue'])));
        handleClose();
    };

    if (max < 0) throw new Error(`Shell: Invalid max "${max}" in component UploadFileDialog.`);

    return (
        <CustomDialog open={open} scroll={'paper'} onClose={handleClose}>
            <DialogTitle>
                <Stack spacing={1}>
                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Text component={'h1'} variant={'h6'} weight={'medium'}>
                            {title}
                        </Text>
                        <IconButton
                            aria-label={'close'}
                            disabled={files.some((file) => file.loading)}
                            kind={'exit'}
                            size={'medium'}
                            variant={'square'}
                            onClick={handleClose}
                        />
                    </Stack>
                    <Text variant={'p3'} weight={'medium'}>
                        {description}
                    </Text>
                </Stack>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Stack spacing={3}>
                    {canStillUploadMore && (
                        <FileUpload accept={allowedFileTypes} multiple={allowMulti} onChange={handleChange} />
                    )}
                    {!hasReachedLimit && (hasMaxLimit || hasError) && (
                        <Stack direction={'row'} justifyContent={'space-between'}>
                            {hasMaxLimit ? (
                                <Text color={'light'} variant={'p3'}>
                                    {t(
                                        'dialog.upload-file.remaining-uploads',
                                        'Remaining uploads: {{remainingUploads}}',
                                        { remainingUploads: numRemainingUploads }
                                    )}
                                </Text>
                            ) : (
                                <Box />
                            )}
                            {hasError ? (
                                <Text color={'error'} variant={'p3'}>
                                    {_error || error}
                                </Text>
                            ) : null}
                        </Stack>
                    )}
                    {files.length ? (
                        <Stack spacing={2}>
                            {files.map((file) => (
                                <ResourceChip
                                    key={file.id}
                                    options={[{ key: 'delete', value: t('common:controls.remove', 'Remove') }]}
                                    resource={file}
                                    size={'medium'}
                                    type={'file'}
                                    onSelect={handleSelect}
                                />
                            ))}
                        </Stack>
                    ) : null}
                </Stack>
            </DialogContent>
            <Divider />
            <DialogActions>
                <Stack
                    sx={{ width: '100%' }}
                    alignItems={'center'}
                    direction={'row-reverse'}
                    justifyContent={'space-between'}
                >
                    <Button
                        loading={loading}
                        loadingGrow={loading}
                        disabled={!files.length || files.some((file) => file.loading) || loading}
                        onClick={handleClickUpload}
                    >
                        {uploadButtonText}
                    </Button>
                    {files.length ? (
                        <Text color={'light'}>
                            {t('dialog.upload-file.files-added', '{{file}} added', {
                                file: files.filter((file) => !file.loading).length,
                            })}
                        </Text>
                    ) : null}
                </Stack>
            </DialogActions>
        </CustomDialog>
    );
};

const CustomDialog = styled(Dialog)(() => {
    return {
        '& .MuiDialog-paper': {
            width: '440px',
            height: '500px',
        },
    };
});

export default UploadFileDialog;
